import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { useGetCanUseFullServiceDealerDropdownQuery } from '../../api/endpoints/compound';

const pick = (obj, keys) => {
    for (const k of keys) {
        const v = obj?.[k];
        if (v !== undefined && v !== null) return v;
    }
    return undefined;
};

const CanUseFullServiceDealerDropdown = ({ value, onChange, className = '', disabled = false, placeholder = 'Bayi seçiniz veya arayınız', itemCount = 50 }) => {
    const [dropdownFilterValue, setDropdownFilterValue] = useState('');
    const [searchText, setSearchText] = useState('');
    const debounceRef = useRef(null);

    // seçili value listede yoksa sadece onu fetch etmek için
    const [specificSearch, setSpecificSearch] = useState('');

    // value primitive olsun
    const dropdownValue = value && typeof value === 'object' ? value.id ?? value.value : value;

    const handleDropdownFilterInputChange = useCallback((val) => {
        setDropdownFilterValue(val);

        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
            setSearchText((val ?? '').trim());
        }, 350);
    }, []);

    useEffect(() => {
        return () => {
            if (debounceRef.current) clearTimeout(debounceRef.current);
        };
    }, []);

    // Kullanıcı arıyorsa searchText, yoksa selected’ı bulmak için specificSearch
    const effectiveSearchText = (searchText || specificSearch || '').toString();

    const { data: dealerList = [], isLoading } = useGetCanUseFullServiceDealerDropdownQuery({
        ItemCount: itemCount,
        SearchText: effectiveSearchText
    });

    const apiOptions = useMemo(() => {
        return (dealerList || [])
            .map((d) => {
                const id = pick(d, ['dealerId', 'DealerId', 'id', 'Id']);
                const name = pick(d, ['dealerName', 'DealerName', 'name', 'Name']) || '';
                if (id === undefined || id === null) return null;
                return { label: String(name), value: Number(id) };
            })
            .filter(Boolean);
    }, [dealerList]);

    const hasSelectedInOptions = useMemo(() => {
        if (dropdownValue === undefined || dropdownValue === null || dropdownValue === '') return true;
        return apiOptions.some((o) => o.value?.toString() === dropdownValue?.toString());
    }, [apiOptions, dropdownValue]);

    useEffect(() => {
        if (searchText) return;

        const isValueEmpty = dropdownValue === undefined || dropdownValue === null || dropdownValue === '';

        if (isValueEmpty) {
            setSpecificSearch('');
        } else if (!hasSelectedInOptions) {
            setSpecificSearch(String(dropdownValue));
        }
    }, [dropdownValue, hasSelectedInOptions, searchText]);

    const mergedOptions = useMemo(() => {
        const base = [...apiOptions];

        if (dropdownValue !== undefined && dropdownValue !== null && dropdownValue !== '') {
            const exists = base.some((x) => x.value?.toString() === dropdownValue?.toString());
            if (!exists) {
                base.unshift({
                    label: `#${dropdownValue}`,
                    value: Number(dropdownValue),
                    __placeholder: true
                });
            }
        }

        const uniq = new Map();
        base.forEach((o) => uniq.set(o.value?.toString(), o));
        return Array.from(uniq.values());
    }, [apiOptions, dropdownValue]);

    const emptyMessage = (dropdownFilterValue ?? '').trim() ? 'Kayıt bulunamadı' : 'Kayıt yok';

    const filterTemplate = useCallback(
        () => (
            <div className="p-dropdown-filter-container" onMouseDown={(e) => e.stopPropagation()}>
                <InputText
                    value={dropdownFilterValue}
                    onChange={(e) => handleDropdownFilterInputChange(e.target.value)}
                    className="p-dropdown-filter w-full"
                    placeholder="Ara"
                    autoFocus
                    disabled={disabled}
                    onKeyDown={(e) => e.stopPropagation()}
                    onKeyUp={(e) => e.stopPropagation()}
                    onClick={(e) => e.stopPropagation()}
                />
                <span className="p-dropdown-filter-icon pi pi-search" />
            </div>
        ),
        [dropdownFilterValue, handleDropdownFilterInputChange, disabled]
    );

    return (
        <Dropdown
            value={dropdownValue ?? null}
            onChange={(e) => {
                onChange?.(e.value ?? null);
                setDropdownFilterValue('');
                setSearchText('');
            }}
            options={mergedOptions}
            optionLabel="label"
            optionValue="value"
            placeholder={placeholder}
            className={className}
            filter
            filterTemplate={filterTemplate}
            showClear
            loading={isLoading}
            disabled={disabled}
            appendTo="self"
            emptyMessage={emptyMessage}
            virtualScrollerOptions={{ itemSize: 38 }}
            itemTemplate={(opt) => (
                <div>
                    <span style={{ fontWeight: 'bold' }}>{opt.value}</span> {opt.label}
                </div>
            )}
        />
    );
};

export default CanUseFullServiceDealerDropdown;
